import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import { fr, type TranslationKey } from './fr';
import { en } from './en';

export type Locale = 'fr' | 'en';
export type { TranslationKey };

/** Permet de traduire un code d'erreur venu du serveur sans risquer une clé inexistante. */
export function isTranslationKey(value: string): value is TranslationKey {
  return value in fr;
}

const DICTIONARIES: Record<Locale, Record<TranslationKey, string>> = { fr, en };
const STORAGE_KEY = 'uhc.locale';

export type Translate = (key: TranslationKey, values?: Record<string, string | number>) => string;

type LocaleContextValue = {
  locale: Locale;
  setLocale: (locale: Locale) => void;
  /** Applique une langue suggérée sans écraser un choix explicite de l'utilisateur. */
  adoptDefault: (locale: string | null | undefined) => void;
  t: Translate;
  formatDate: (value: string | Date | null | undefined) => string;
  formatDuration: (until: string | Date) => string;
};

const LocaleContext = createContext<LocaleContextValue | null>(null);

function initialLocale(): Locale {
  const stored = localStorage.getItem(STORAGE_KEY);
  if (stored === 'fr' || stored === 'en') return stored;
  return navigator.language.toLowerCase().startsWith('en') ? 'en' : 'fr';
}

export function LocaleProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(initialLocale);

  const setLocale = useCallback((next: Locale) => {
    localStorage.setItem(STORAGE_KEY, next);
    document.documentElement.lang = next;
    setLocaleState(next);
  }, []);

  /**
   * Langue issue du profil Discord.
   *
   * Elle ne s'applique que si l'utilisateur n'a jamais choisi lui-même : un choix
   * explicite doit survivre à une reconnexion, sinon le sélecteur donne l'impression
   * de ne pas fonctionner.
   */
  const adoptDefault = useCallback((suggested: string | null | undefined) => {
    if (localStorage.getItem(STORAGE_KEY)) return;
    const normalized = suggested?.toLowerCase().startsWith('en') ? 'en' : 'fr';
    document.documentElement.lang = normalized;
    setLocaleState(normalized);
  }, []);

  const value = useMemo<LocaleContextValue>(() => {
    const dictionary = DICTIONARIES[locale];

    /** Interpolation minimale : `{nom}` remplacé par la valeur fournie. */
    const t: Translate = (key, values) => {
      const template = dictionary[key];
      if (!values) return template;
      return template.replace(/\{(\w+)\}/g, (match, name: string) =>
        name in values ? String(values[name]) : match,
      );
    };

    const formatDate = (input: string | Date | null | undefined): string => {
      if (!input) return t('common.never');
      const date = typeof input === 'string' ? new Date(input) : input;
      return new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeStyle: 'short' }).format(
        date,
      );
    };

    /** Durée restante en langage courant : « dans 23 heures » plutôt qu'un horodatage. */
    const formatDuration = (until: string | Date): string => {
      const target = typeof until === 'string' ? new Date(until) : until;
      const seconds = Math.max(0, Math.round((target.getTime() - Date.now()) / 1000));
      const formatter = new Intl.RelativeTimeFormat(locale, { numeric: 'auto', style: 'long' });
      if (seconds < 90) return formatter.format(seconds, 'second').replace(/^(dans |in )/, '');
      if (seconds < 5400)
        return formatter.format(Math.round(seconds / 60), 'minute').replace(/^(dans |in )/, '');
      if (seconds < 172_800)
        return formatter.format(Math.round(seconds / 3600), 'hour').replace(/^(dans |in )/, '');
      return formatter.format(Math.round(seconds / 86_400), 'day').replace(/^(dans |in )/, '');
    };

    return { locale, setLocale, adoptDefault, t, formatDate, formatDuration };
  }, [locale, setLocale, adoptDefault]);

  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
}

export function useI18n(): LocaleContextValue {
  const context = useContext(LocaleContext);
  if (!context) throw new Error('useI18n doit être utilisé dans un LocaleProvider');
  return context;
}
